/* eslint-disable jira/choreographer/message-id-by-folder-structure */

import React, { type ComponentType, type ReactNode, useCallback, useState } from 'react';
import type { Team as TeamType } from '@jira/issue__types/src/Team.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import { isTeamsAppEnabled, toTeamsAppURL } from '@jira/platform__teams-app-config/src/index.tsx';
import prsLocaleMapper from '@atlaskit/prs-locale-mapper';
import { useFlagsService } from '@atlassian/jira-flags';
import ErrorFlag from '@atlassian/jira-flags/src/common/ui/components/error-flag';
import SuccessFlag from '@atlassian/jira-flags/src/common/ui/components/success-flag';
import WarningFlag from '@atlassian/jira-flags/src/common/ui/components/warning-flag';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Team, TeamCreateDialogProps, Flag } from '@atlassian/people-teams';
import { PeopleAndTeamsConfigurationProvider } from '@atlassian/people-teams-configuration-client';
import { lazy, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { useRouterActions } from '@atlassian/react-resource-router';

const openInNewTab = (url: string): void => {
	if (typeof window !== 'undefined') {
		window.open(url, '_blank');
	}
};

type TeamValue = Readonly<TeamType> | null | undefined;
type TriggerCreateTeamDialogCallback = (teamName: string) => void;

// Relay entry point is not applicable here because this component does not live in jira frontend.
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const TeamCreateDialog: LazyComponent<React.ComponentType<TeamCreateDialogProps>> = lazy<
	ComponentType<TeamCreateDialogProps>
>(() =>
	import(
		/* webpackChunkName: "async-lazy-jira-issue-field-team-create-dialog" */ '@atlassian/people-teams'
	).then((module) => module.TeamCreateDialog),
);

export interface CreateTeamDialogProps {
	onCreate?: (team: TeamValue) => void;
	onCancel?: () => void;
	analyticsCtx?: object;
	handleAdditionalActionStart?: () => void;
}

export type CreateTeamDialogReturn = [ReactNode, TriggerCreateTeamDialogCallback];

export const useCreateTeamDialog = (props: CreateTeamDialogProps = {}): CreateTeamDialogReturn => {
	const { locale } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [inputTeamName, setInputTeamName] = useState('');
	const { showFlag } = useFlagsService();
	const { push } = useRouterActions();
	const { handleAdditionalActionStart } = props;
	const tenantContext = useTenantContext();
	const { cloudId, atlassianAccountId } = tenantContext;
	const { data: orgId } = useOrgId();
	const {
		data: { user },
	} = useCurrentUser();

	if (!atlassianAccountId && !user.accountId) {
		throw new Error('Missing current user id');
	}

	const navigateTo = useCallback(
		(url: string) => {
			if (isTeamsAppEnabled()) {
				openInNewTab(toTeamsAppURL(url.replace('/jira', '')));
			} else {
				push(url);
			}
		},
		[push],
	);

	const addFlag = useCallback(
		({ appearance, ...flagProps }: Flag) => {
			if (appearance === 'success' || appearance === 'normal') {
				showFlag((serviceProps) => (
					<SuccessFlag
						messageId="issue-field-create-team-dialog.ui.use-create-team-dialog.success-flag"
						messageType="transactional"
						{...flagProps}
						{...serviceProps}
					/>
				));
			}

			if (appearance === 'warning') {
				showFlag((serviceProps) => (
					<WarningFlag
						messageId="issue-field-create-team-dialog.ui.use-create-team-dialog.warning-flag"
						messageType="transactional"
						{...flagProps}
						{...serviceProps}
					/>
				));
			}

			if (appearance === 'error') {
				showFlag((serviceProps) => (
					<ErrorFlag
						messageId="issue-field-create-team-dialog.ui.use-create-team-dialog.error-flag"
						messageType="transactional"
						{...flagProps}
						{...serviceProps}
					/>
				));
			}
		},
		[showFlag],
	);

	const { onCancel } = {
		onCancel: () => null,
		...props,
	};

	const callback: TriggerCreateTeamDialogCallback = useCallback(
		(teamName: string) => {
			setInputTeamName(teamName);
			setIsOpen(true);
			handleAdditionalActionStart?.();
		},
		[handleAdditionalActionStart],
	);

	const handleOnClose = () => {
		setIsOpen(false);
		onCancel();
	};

	const handleOnCreate = (team: Team) => {
		const formattedTeam: TeamValue = {
			...team,
			name: team.displayName,
		};

		props.onCreate?.(formattedTeam);
	};

	return [
		isOpen ? (
			<Placeholder name="team-create-dialog">
				<PeopleAndTeamsConfigurationProvider product="jira" locale={prsLocaleMapper(locale)}>
					<TeamCreateDialog
						cloudId={cloudId}
						orgId={orgId}
						product="jira"
						principalId={atlassianAccountId || user.accountId || ''}
						onClose={handleOnClose}
						onCreateTeamSuccess={handleOnCreate}
						extraAnalyticsAttrs={props.analyticsCtx}
						enableMembershipSettingsChoice
						currentUser={
							user.accountId
								? {
										id: user.accountId,
										fullName: user.userFullname,

										avatarUrl:
											('avatarUrls' in user && user.avatarUrls && user.avatarUrls['48x48']) ||
											undefined,
										email:
											'emailAddress' in user && user.emailAddress ? user.emailAddress : undefined,
										...user,
									}
								: undefined
						}
						prepopulatedFields={{
							teamName: inputTeamName,
						}}
						pushRoute={navigateTo}
						addFlag={addFlag}
						{...(fg('platform_create_work_item_modernization') ? { headingAs: 'h1' } : {})}
					/>
				</PeopleAndTeamsConfigurationProvider>
			</Placeholder>
		) : null,
		callback,
	];
};
